import { FC, useCallback, useState } from "react";
import * as React from "react";
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { FormikProvider, useFormik, FormikConfig } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { SubmittedEmailOutput } from "apiIntegrations/campaigns/SubmittedEmail.interface";
import { email } from "validators/client/email";

import { INFO_BOX_TYPE } from "components/InfoBox/types";
import InfoBox from "components/InfoBox";
import CustomOutlinedInput from "components/CustomOutlinedInput";
import CustomButton from "components/CustomButton";
import Recaptcha from "components/Recaptcha";

import { STORAGE } from "enums/storage";

import { API_ROUTES } from "config/routes";

import { getObjectItem, setObjectItem } from "utils/storage";

import fetcher from "lib/fetchJson";

interface AddressSubmission {
  email: string;
}

const initialState: AddressSubmission = {
  email: "",
};

interface SectionEmailProps {
  canMint: boolean;
  bundleId: number;
  html: string;
  buttonText: string;
  successMessage: string;
  isMinted?: boolean;
  isMintGated?: boolean;
  mintGatedText?: string;
}

const SectionEmail: FC<SectionEmailProps> = ({
  canMint,
  bundleId,
  html,
  buttonText,
  successMessage,
  isMinted,
  isMintGated,
  mintGatedText,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const storageData = getObjectItem<AddressSubmission>(STORAGE.EMAIL);
  const onSubmit: FormikConfig<AddressSubmission>["onSubmit"] = useCallback(
    async (values) => {
      try {
        if (!executeRecaptcha) {
          return Promise.reject({
            message: "Execute recaptcha not yet available",
          });
        }
        const captchaToken = await executeRecaptcha("SUBMIT_EMAIL");
        setObjectItem(STORAGE.EMAIL, values);
        const { data } = await fetcher<{ data: SubmittedEmailOutput }>(
          API_ROUTES.SUBMIT_EMAIL,
          {
            ...values,
            bundleId,
            captchaToken,
          },
          "POST",
        );
        setIsSubmitted(!!data.success);
      } catch (e) {
        toast.error(e.data.message ?? "Something went wrong.");
      }
    },
    [executeRecaptcha, bundleId],
  );

  const formik = useFormik({
    initialValues: storageData ?? initialState,
    validationSchema: toFormikValidationSchema(email),
    validateOnChange: false,
    onSubmit: onSubmit,
  });

  if (isSubmitted) {
    return (
      <Box>
        <InfoBox type={INFO_BOX_TYPE.SUCCESS}>{successMessage}</InfoBox>
      </Box>
    );
  }

  if (!isMinted && isMintGated) {
    return (
      <Box display="grid" gap={4}>
        <InfoBox type={INFO_BOX_TYPE.INFO}>{mintGatedText}</InfoBox>
      </Box>
    );
  }

  return (
    <FormikProvider value={formik}>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Typography dangerouslySetInnerHTML={{ __html: html }} mb={2} />
        <CustomOutlinedInput
          label="Email"
          inputProps={{
            id: "email",
            placeholder: "Email",
            value: formik.values.email,
            onChange: formik.handleChange,
          }}
          formControlProps={{
            fullWidth: true,
            margin: "normal",
          }}
          error={formik.errors.email}
        />
        <Recaptcha boxProps={{ mb: 2 }} />

        <CustomButton
          sx={{
            textTransform: "uppercase",
            ml: "auto",
            display: "block",
          }}
          type="submit"
          isLoading={formik.isSubmitting}
          disabled={formik.isSubmitting || !canMint}
          color="primary"
        >
          {buttonText}
        </CustomButton>
      </Box>
    </FormikProvider>
  );
};

export default SectionEmail;
